import React, { useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Alert,
  Chip,
  Grid,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import SearchIcon from '@mui/icons-material/Search'
import BusinessIcon from '@mui/icons-material/Business'
import PaymentsIcon from '@mui/icons-material/Payments'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CleaningServicesIcon from '@mui/icons-material/CleaningServices'
import API from '../../services/API'
import PageHeader from '../kit/PageHeader'
import StatTile from '../kit/StatTile'
import dataGridSx from '../kit/dataGridSx'
import { SoftChip, StageChip, TrialledChip } from '../kit/Chips'
import {
  formatCurrency,
  formatDate,
  fromNow,
  lastActivityColour,
  monthlyFee,
  subscriptionChip,
  tierLabel,
} from '../kit/helpers'
import { ACTION_STATUS, OPEN_ACTION_STATUSES } from './CommercialUsage/constants'

const PAYMENT_ISSUE = ['past_due', 'unpaid', 'incomplete', 'incomplete_expired', 'paused']

const FILTERS = [
  { key: 'active', label: 'Active', predicate: (row) => row.activeSubscription && !row.isDemo },
  {
    key: 'attention',
    label: 'Payment issue',
    predicate: (row) => row.activeSubscription && PAYMENT_ISSUE.includes(row.stripeSubscriptionStatus),
  },
  {
    key: 'cancelling',
    label: 'Cancelling',
    predicate: (row) => row.activeSubscription && !!row.subscriptionCancellationDate,
  },
  {
    key: 'notSubscribed',
    label: 'Not subscribed',
    predicate: (row) => !row.activeSubscription && !row.isDemo,
  },
  { key: 'all', label: 'All tenants', predicate: () => true },
]

const Tenants = () => {
  const [tenants, setTenants] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()

  const filterKey = searchParams.get('filter') || 'active'
  const filter = FILTERS.find((option) => option.key === filterKey) ?? FILTERS[0]
  const setFilter = (key) => {
    const next = new URLSearchParams(searchParams)
    if (key === 'active') next.delete('filter')
    else next.set('filter', key)
    setSearchParams(next, { replace: true })
  }

  useEffect(() => {
    API.get('/admincrm/tenants')
      .then((res) => {
        if (res.status === 200) setTenants(res.data)
        else setError(res.data?.message || 'Could not load tenants.')
      })
      .finally(() => setLoading(false))
  }, [])

  const stats = useMemo(() => {
    const active = tenants.filter(FILTERS[0].predicate)
    return {
      active: active.length,
      mrr: active.reduce((total, tenant) => total + (monthlyFee(tenant.contract) ?? 0), 0),
      attention: tenants.filter(FILTERS[1].predicate).length,
      notSubscribed: tenants.filter(FILTERS[3].predicate).length,
    }
  }, [tenants])

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase()
    return tenants.filter(filter.predicate).filter((tenant) => {
      if (!query) return true
      return [
        tenant.name,
        tenant.subdomain,
        tenant.mainContact?.email,
        tenant.mainContact?.firstName,
        tenant.mainContact?.lastName,
        tenant.contract?.packageName,
        tenant.region,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(query)
    })
  }, [tenants, filter, search])

  const showingInactive = filterKey === 'notSubscribed' || filterKey === 'all'

  const columns = [
    {
      field: 'name',
      headerName: 'Organisation',
      minWidth: 240,
      flex: 1.2,
      renderCell: ({ row }) => (
        <Stack minWidth={0}>
          <Stack direction="row" gap={0.75} alignItems="center" minWidth={0}>
            <Typography fontWeight={700} noWrap>
              {row.name}
            </Typography>
            {row.isDemo && <SoftChip color="#85276A" label="Demo" sx={{ height: 18, fontSize: 10 }} />}
          </Stack>
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.subdomain}.{process.env.ROOT_DOMAIN}
            {row.region ? ` · ${row.region}` : ''}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'contact',
      headerName: 'Main contact',
      minWidth: 200,
      flex: 0.9,
      valueGetter: (value, row) =>
        [row.mainContact?.firstName, row.mainContact?.lastName].filter(Boolean).join(' '),
      renderCell: ({ value, row }) => (
        <Stack minWidth={0}>
          <Typography variant="body2" color="text.primary" noWrap>
            {value || '—'}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {row.mainContact?.email || ''}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'package',
      headerName: 'Plan',
      minWidth: 130,
      flex: 0.5,
      valueGetter: (value, row) =>
        row.activeSubscription ? row.contract?.packageName || tierLabel(row.subscriptionTier) : '',
      // An open commercial usage action (advisory / quote / upgrade required)
      // is flagged under the plan it concerns.
      renderCell: ({ value, row }) => {
        const usageStatus = row.contract?.usageReview?.status
        return (
          <Stack minWidth={0} alignItems="flex-start" gap={0.25}>
            <Typography variant="body2" color={value ? 'text.primary' : 'text.disabled'} noWrap>
              {value || '—'}
            </Typography>
            {OPEN_ACTION_STATUSES.includes(usageStatus) && (
              <SoftChip
                color={ACTION_STATUS[usageStatus].color}
                label={ACTION_STATUS[usageStatus].label}
                title="Commercial usage review - see the tenant's Commercial usage tab"
                sx={{ height: 18, fontSize: 10 }}
              />
            )}
          </Stack>
        )
      },
    },
    {
      field: 'monthlyFee',
      headerName: 'Monthly',
      minWidth: 100,
      flex: 0.4,
      type: 'number',
      valueGetter: (value, row) => (row.activeSubscription ? monthlyFee(row.contract) : null),
      renderCell: ({ value, row }) => (
        <Typography variant="body2" color={value ? 'text.primary' : 'text.disabled'}>
          {value === null || value === undefined ? '—' : formatCurrency(value, row.contract?.currency)}
        </Typography>
      ),
    },
    {
      field: 'userCount',
      headerName: 'Users',
      minWidth: 80,
      flex: 0.3,
      type: 'number',
      renderCell: ({ value }) => (value === null || value === undefined ? '—' : value),
    },
    {
      field: 'lastUserActivity',
      headerName: 'Last activity',
      minWidth: 130,
      flex: 0.5,
      valueGetter: (value) => (value ? new Date(value) : null),
      sortComparator: (a, b) => (a?.getTime() ?? 0) - (b?.getTime() ?? 0),
      renderCell: ({ value }) => (
        <Typography variant="body2" sx={{ color: lastActivityColour(value) }}>
          {value ? fromNow(value) : 'Never'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      minWidth: 140,
      flex: 0.5,
      valueGetter: (value, row) => subscriptionChip(row).label,
      renderCell: ({ row }) => {
        const chip = subscriptionChip(row)
        return (
          <Chip
            label={chip.label}
            color={chip.color}
            variant={chip.variant ?? 'filled'}
            size="small"
            sx={{ borderRadius: 1.5 }}
          />
        )
      },
    },
    showingInactive
      ? {
          field: 'trialEnd',
          headerName: 'Trial / signed up',
          minWidth: 170,
          flex: 0.6,
          valueGetter: (value, row) => (value ? new Date(value) : row.createdAt ? new Date(row.createdAt) : null),
          sortComparator: (a, b) => (a?.getTime() ?? 0) - (b?.getTime() ?? 0),
          renderCell: ({ row }) =>
            row.trialEnd ? (
              <TrialledChip trialEnd={row.trialEnd} />
            ) : (
              <Typography variant="body2" color="text.secondary">
                Signed up {formatDate(row.createdAt)}
              </Typography>
            ),
        }
      : {
          field: 'renewal',
          headerName: 'Renewal',
          minWidth: 130,
          flex: 0.5,
          valueGetter: (value, row) => {
            const date = row.contract?.renewalDate || row.stripeSubscriptionExpiry
            return date ? new Date(date) : null
          },
          sortComparator: (a, b) => (a?.getTime() ?? Infinity) - (b?.getTime() ?? Infinity),
          renderCell: ({ value }) => {
            if (!value) return <Typography variant="body2" color="text.disabled">—</Typography>
            const expired = moment(value).startOf('day').isSameOrBefore(moment().startOf('day'))
            const soon = !expired && moment(value).diff(moment(), 'days') <= 14
            return (
              <Typography
                variant="body2"
                fontWeight={expired || soon ? 700 : 400}
                sx={{ color: expired ? 'anRed.main' : soon ? 'anOrange.main' : 'text.primary' }}
              >
                {formatDate(value)}
              </Typography>
            )
          },
        },
    {
      field: 'leadStage',
      headerName: 'Sales',
      minWidth: 110,
      flex: 0.4,
      renderCell: ({ value }) =>
        value ? (
          <StageChip stage={value} />
        ) : (
          <Typography variant="caption" color="text.disabled">
            No lead
          </Typography>
        ),
    },
  ]

  return (
    <Stack gap={2.5}>
      <PageHeader
        eyebrow="Customers"
        title="Tenants"
        subtitle="Every organisation with an AmbuNet account, live or not."
      />

      <Grid container spacing={1.5}>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Active subscribers"
            value={stats.active}
            caption="Paying tenants"
            icon={<BusinessIcon />}
            tone="#00964C"
            active={filterKey === 'active'}
            onClick={() => setFilter('active')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Monthly revenue"
            value={formatCurrency(stats.mrr)}
            caption="Contracted fees, annual ÷ 12"
            icon={<PaymentsIcon />}
            tone="#239AA4"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Payment issues"
            value={stats.attention}
            caption="Past due, unpaid or paused"
            icon={<WarningAmberIcon />}
            tone={stats.attention ? '#E32525' : '#64748B'}
            active={filterKey === 'attention'}
            onClick={() => setFilter('attention')}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <StatTile
            label="Not subscribed"
            value={stats.notSubscribed}
            caption="Ex-trial and lapsed - awaiting cleanup"
            icon={<CleaningServicesIcon />}
            tone="#F28B1F"
            active={filterKey === 'notSubscribed'}
            onClick={() => setFilter('notSubscribed')}
          />
        </Grid>
      </Grid>

      <Stack direction={{ xs: 'column', md: 'row' }} gap={1.5} alignItems={{ md: 'center' }}>
        <Stack direction="row" gap={0.75} flexWrap="wrap" flex={1}>
          {FILTERS.map((option) => (
            <Chip
              key={option.key}
              label={option.label}
              clickable
              onClick={() => setFilter(option.key)}
              color={filterKey === option.key ? 'primary' : 'default'}
              variant={filterKey === option.key ? 'filled' : 'outlined'}
              sx={{ borderRadius: 2, fontWeight: 600, bgcolor: filterKey === option.key ? undefined : '#FFFFFF' }}
            />
          ))}
        </Stack>
        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search tenants…"
          size="small"
          sx={{ minWidth: { md: 300 }, bgcolor: '#FFFFFF' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}
      {filterKey === 'notSubscribed' && !loading && (
        <Alert severity="info">
          These tenants have no active subscription. Run{' '}
          <code>scripts/capture-inactive-tenants-as-leads.mjs</code> to copy them into the sales
          pipeline before their records and databases are removed - each one is also reachable from
          the Pipeline as an "open as lead" row.
        </Alert>
      )}

      <DataGrid
        rows={rows}
        columns={columns}
        loading={loading}
        autoHeight
        rowHeight={60}
        getRowId={(row) => row._id}
        disableRowSelectionOnClick
        onRowClick={({ row }) => navigate(`/tenants/${row._id}`)}
        initialState={{
          sorting: { sortModel: [{ field: showingInactive ? 'lastUserActivity' : 'name', sort: showingInactive ? 'desc' : 'asc' }] },
          pagination: { paginationModel: { pageSize: 25 } },
        }}
        pageSizeOptions={[25, 50, 100]}
        sx={dataGridSx}
      />
    </Stack>
  )
}

export default Tenants
